'use client';

import dayjs from 'dayjs';
import React, { useMemo, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useAppContext } from 'src/context/app';
import { useAuthContext } from 'src/auth/hooks';
import { DashboardContent } from 'src/layouts/dashboard';
import { NyttFärgTema } from 'src/theme/NyttFärgtema';

import Välkomstbanner from '../valkomst/valkomstbanner';
import { härledVälkomstläge } from '../valkomst/valkomstlage';
import DomainCard from '../components/domain-card';
import AttGöraSond from '../alla-organisationer/AttGöraSond';
import Åtgärdslista, { sorteraÅtgärder } from '../alla-organisationer/Åtgärdslista';
import { useAllaÖversikt } from '../alla-organisationer/ÖversiktService';

import type { SondRapport } from '../alla-organisationer/AttGöraSond';
import type { Åtgärdsrad, Allvarlighetsgrad } from '../alla-organisationer/Åtgärdslista';
import type { IOrganisation } from 'src/types/nya/common';

// ----------------------------------------------------------------------

function ärGrad(värde: string | undefined): värde is Allvarlighetsgrad {
  return värde === 'INFO' || värde === 'VARNING' || värde === 'BRÅDSKANDE';
}

// ----------------------------------------------------------------------

/**
 * Appens startsida i läget "Alla organisationer".
 *
 * <p>Vyn svarar på en enda fråga: vad behöver göras? Den som är med i flera
 * föreningar såg tidigare bara en av dem åt gången, och de övrigas förfallodagar
 * fanns ingenstans — startsidan bad i stället om ett föreningsval innan den visade
 * något alls. Här slås föreningarna ihop till en lista i den ordning sakerna
 * behöver göras, med föreningens namn på varje rad.
 *
 * <p>Två datakällor möts:
 * <ul>
 *   <li>Backendens aggregat (`/api/oversikt/v1/alla`) för det som finns i databasen
 *       — obokförda transaktioner, omatchade inköp, mail i inkorgen.
 *   <li>Regelmotorn i `myndighetsdatum.ts` för förfallodagarna, som räknas ut
 *       lokalt eftersom det inte finns något deadline-API att fråga. Den når hubben
 *       via en {@link AttGöraSond} per förening.
 * </ul>
 */
export function AllaOrganisationerHubb() {
  const router = useRouter();
  const { anvandare, organisationer } = useAuthContext();
  const { uppdateraValdOrganisation } = useAppContext();

  const föreningar: IOrganisation[] = useMemo(() => organisationer ?? [], [organisationer]);

  const { översikt, översiktLoading } = useAllaÖversikt();

  // Sondernas rapporter, en per förening plus en för personens egna datum.
  const [sondrapporter, setSondrapporter] = useState<Record<string, SondRapport>>({});

  // Funktionell uppdatering och ingen beroendelista: callbacken måste ha stabil
  // identitet, annars triggar varje sonds effekt en ny rendering som ger en ny
  // callback som triggar effekten igen.
  const taEmotRapport = useCallback((nyckel: string, rapport: SondRapport) => {
    setSondrapporter((tidigare) => {
      const befintlig = tidigare[nyckel];
      if (befintlig && befintlig.poster === rapport.poster) return tidigare;
      return { ...tidigare, [nyckel]: rapport };
    });
  }, []);

  const rader: Åtgärdsrad[] = useMemo(() => {
    const samlade: Åtgärdsrad[] = [];

    // Databasens åtgärder, per förening.
    översikt.organisationer.forEach((org) => {
      org.åtgärder?.forEach((åtgärd) => {
        samlade.push({
          id: `${org.orgId}:${åtgärd.typ}`,
          rubrik: åtgärd.rubrik,
          orgId: org.orgId,
          orgNamn: org.orgNamn ?? undefined,
          grad: ärGrad(åtgärd.allvarlighetsgrad) ? åtgärd.allvarlighetsgrad : 'INFO',
          länk: åtgärd.länk,
          internLänk: true,
        });
      });
    });

    // Förfallodagarna, från sonderna.
    Object.entries(sondrapporter).forEach(([nyckel, rapport]) => {
      rapport.poster.forEach((post) => {
        const försenad = post.förfallodag.isBefore(new Date(), 'day');
        samlade.push({
          id: `${nyckel}:${post.id}`,
          rubrik: post.titel,
          detalj: [post.avsändare, post.förfallodag.format('D MMM YYYY')]
            .filter(Boolean)
            .join(' · '),
          orgId: rapport.organisation?.id,
          orgNamn: rapport.organisation?.organisationsNamn,
          grad: försenad ? 'BRÅDSKANDE' : 'VARNING',
          förfallodag: post.förfallodag,
          länk: post.länk,
          internLänk: post.internLänk,
        });
      });
    });

    return sorteraÅtgärder(samlade);
  }, [översikt, sondrapporter]);

  // Samma dag som sonderna räknar med; hubben lever sällan över midnatt.
  const idag = useMemo(() => dayjs().startOf('day'), []);

  const välkomstläge = useMemo(
    () =>
      härledVälkomstläge({
        laddar: översiktLoading,
        poster: rader.map((rad) => ({ ...rad, titel: rad.rubrik })),
        idag,
        antalOrganisationer: föreningar.length,
      }),
    [översiktLoading, rader, idag, föreningar.length]
  );

  /**
   * Byter till radens förening innan navigeringen, annars landar användaren på rätt
   * sida i fel förening — samma ordning som `useNotifieringsÖppnare` håller.
   */
  const öppnaÅtgärd = useCallback(
    (rad: Åtgärdsrad) => {
      if (rad.orgId) {
        const mål = föreningar.find((org) => org.id === rad.orgId);
        if (mål) uppdateraValdOrganisation(mål);
      }
      if (!rad.länk) return;
      if (rad.internLänk === false) {
        window.open(rad.länk, '_blank', 'noopener');
        return;
      }
      router.push(rad.länk);
    },
    [föreningar, router, uppdateraValdOrganisation]
  );

  const öppnaFörening = useCallback(
    (org: IOrganisation) => {
      uppdateraValdOrganisation(org);
      router.push(paths.dashboard.root);
    },
    [router, uppdateraValdOrganisation]
  );

  return (
    <DashboardContent maxWidth="xl">
      {/*
        Sonderna renderar ingenting. De finns för att få anropa `useAttGöra` en gång
        per förening — en hook går inte att loopa. Den utan organisation ger personens
        egna datum, t.ex. Inkomstdeklaration 1.
      */}
      <AttGöraSond onRapport={taEmotRapport} />
      {föreningar.map((org) => (
        <AttGöraSond key={org.id} organisation={org} onRapport={taEmotRapport} />
      ))}

      <Stack spacing={3}>
        <Välkomstbanner
          läge={välkomstläge}
          idag={idag}
          förnamn={anvandare?.namn?.förnamn}
          onÖppna={(post) => öppnaÅtgärd({ ...post, rubrik: post.titel, grad: 'INFO' })}
        />

        <Åtgärdslista rader={rader} laddar={översiktLoading} onÖppna={öppnaÅtgärd} />

        {föreningar.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              Dina organisationer
            </Typography>

            <Grid container spacing={3}>
              {föreningar.map((org, index) => {
                const rad = översikt.organisationer.find((post) => post.orgId === org.id);
                const antalÅtgärder = rad?.åtgärder?.length ?? 0;
                const tema = NyttFärgTema.Kombinationer[index % NyttFärgTema.Kombinationer.length];

                return (
                  <Grid key={org.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Box
                      onClick={() => öppnaFörening(org)}
                      sx={{ height: '100%', cursor: 'pointer' }}
                    >
                      <DomainCard
                        title={org.organisationsNamn}
                        icon="solar:buildings-3-bold"
                        theme={tema}
                        href={paths.dashboard.root}
                        openLabel="Öppna föreningen"
                        statusChip={
                          rad?.fel
                            ? { label: 'KUNDE INTE LÄSAS', color: tema.secondary.hexkod }
                            : undefined
                        }
                        primaryMetric={
                          rad && !rad.fel
                            ? {
                                value: antalÅtgärder,
                                label: antalÅtgärder === 1 ? 'sak att göra' : 'saker att göra',
                              }
                            : undefined
                        }
                        secondaryMetric={
                          rad?.nyckeltal?.antalIInkorgen != null
                            ? { value: rad.nyckeltal.antalIInkorgen, label: 'i inkorgen' }
                            : undefined
                        }
                        emptyMessage={rad?.fel ?? undefined}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            Ditt eget
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DomainCard
                title="Mina hem"
                icon="solar:home-smile-bold"
                theme={NyttFärgTema.Kombinationer[2]}
                href={paths.dashboard.fastighet.minaHem}
                openLabel="Öppna mina hem"
                emptyMessage="El, sophämtning, paket och kollektivtrafik för din adress."
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <DomainCard
                title="Mina fastigheter"
                icon="solar:map-point-bold"
                theme={NyttFärgTema.Kombinationer[4]}
                href={paths.dashboard.fastighet.minaFastigheter}
                openLabel="Öppna mina fastigheter"
                emptyMessage="Fastigheter du äger eller är delägare i, med offerter och underhåll."
              />
            </Grid>
          </Grid>
        </Box>

      </Stack>
    </DashboardContent>
  );
}
